// Extrato da VENDA CM que vai pro CLIENTE (DA-417) — o documento que ele confere ANTES de a loja
// fechar o mes e emitir a nota.
//
// NAO e o extrato do Contas a Receber, e a diferenca nao e cosmetica: aquele responde "quais
// titulos voce deve", com vencimento e dias de atraso; este responde "qual material esta com voce
// e ainda nao foi faturado". Nada aqui e divida ainda — vira, no fechamento.
//
// Separado POR PEDIDO (decisao do Ruan), com a data da retirada e QUEM retirou: quem le do outro
// lado precisa bater com as idas ao balcao ao longo do mes. Unificado (que e como a nota vai sair)
// seria um bolo unico onde ninguem identifica qual retirada esta errada.
//
// So o LAYOUT mora aqui. Impressao e conversao pra JPG vem de `utils/html-para-jpg`, a mesma peca
// que o extrato do CR usa.

export interface ItemExtratoConsignacao {
  codigo: string | null;
  descricao: string | null;
  quantidade: string;
  preco_unitario: string;
  saldo_valor: string;
}

export interface PedidoExtratoConsignacao {
  numero: string;
  liberado_em: string;
  comprador: string | null;
  total_saldo_valor: string;
  itens: ItemExtratoConsignacao[];
}

export interface ExtratoConsignacaoParams {
  clienteNome: string;
  pedidos: PedidoExtratoConsignacao[];
  total: number;
  /** Rodape de paginacao — omitido quando cabe tudo numa pagina. */
  pagina?: { atual: number; total: number; mostrarTotal: boolean };
}

const fmt = (v: number | string | null | undefined): string =>
  Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtQtd = (v: string | number): string => {
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
};

const fmtData = (d: string | null | undefined): string => {
  if (!d) return '—';
  // Aceita as DUAS grafias que a API devolve (texto do Postgres com fuso, ou ISO com Z) — a mesma
  // razao do formatDataSP do shared. Aqui basta a data, entao corta antes de virar Date.
  const so = String(d).slice(0, 10);
  const [a, m, dia] = so.split('-');
  return a && m && dia ? `${dia}/${m}/${a}` : '—';
};

const esc = (s: string | null | undefined): string =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Limite suave por pagina pro JPG nao ficar ilegivel no WhatsApp. Item = 1 linha, pedido = 3. */
const MAX_LINHAS_POR_PAGINA = 80;

export function paginarPedidos(pedidos: PedidoExtratoConsignacao[]): PedidoExtratoConsignacao[][] {
  const paginas: PedidoExtratoConsignacao[][] = [];
  let atual: PedidoExtratoConsignacao[] = [];
  let linhas = 0;
  for (const p of pedidos) {
    const custo = (p.itens?.length || 1) + 3;
    if (atual.length > 0 && linhas + custo > MAX_LINHAS_POR_PAGINA) {
      paginas.push(atual); atual = []; linhas = 0;
    }
    atual.push(p);
    linhas += custo;
  }
  if (atual.length > 0) paginas.push(atual);
  return paginas.length > 0 ? paginas : [[]];
}

export function gerarExtratoConsignacaoHTML(p: ExtratoConsignacaoParams): string {
  const hoje = new Date().toLocaleDateString('pt-BR');

  const blocos = p.pedidos.map((ped) => {
    const linhas = (ped.itens ?? []).map((it) => `
      <tr>
        <td class="cod">${esc(it.codigo) || '—'}</td>
        <td>${esc(it.descricao) || '—'}</td>
        <td class="num">${fmtQtd(it.quantidade)}</td>
        <td class="num">${fmt(it.preco_unitario)}</td>
        <td class="num b">${fmt(it.saldo_valor)}</td>
      </tr>`).join('');

    return `
      <div class="bloco">
        <div class="bloco-head">
          <span class="ped">${esc(ped.numero)}</span>
          <span class="meta">retirado em ${fmtData(ped.liberado_em)}${ped.comprador ? ` &middot; por <b>${esc(ped.comprador)}</b>` : ''}</span>
          <!-- Sem quem retirou, o " · por" some inteiro em vez de virar "por —": no documento do
               cliente um travessao solto parece dado perdido, nao ausencia. -->
          <span class="bloco-total">R$ ${fmt(ped.total_saldo_valor)}</span>
        </div>
        <table class="itens">
          <colgroup>
            <col style="width:58px"><col><col style="width:70px"><col style="width:90px"><col style="width:95px">
          </colgroup>
          <thead>
            <tr><th class="cod">Cód.</th><th>Produto</th><th class="num">Qtd</th><th class="num">Unit.</th><th class="num">Total</th></tr>
          </thead>
          <tbody>${linhas || '<tr><td colspan="5" class="vazio">Sem itens em aberto</td></tr>'}</tbody>
        </table>
      </div>`;
  }).join('');

  const rodapeTotal = (!p.pagina || p.pagina.mostrarTotal) ? `
    <div class="total-geral">
      <span>TOTAL EM ABERTO</span>
      <span class="valor">R$ ${fmt(p.total)}</span>
    </div>` : '';

  const rodapePagina = p.pagina && p.pagina.total > 1
    ? `<div class="pagina">Pagina ${p.pagina.atual} de ${p.pagina.total}</div>` : '';

  return `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="UTF-8"><title>Extrato — ${esc(p.clienteNome)}</title>
<style>
  * { box-sizing: border-box; }
  /* LARGURA DE DOCUMENTO, nao de janela. Sem isto a tabela (que e width:100%) estica junto com a
     janela aberta pelo Imprimir, fica mais larga que a folha e o Chrome TRAVA no "Salvando..." ao
     gerar o PDF. O extrato do CR escapa por acaso — o conteudo dele e estreito. */
  body { font-family: Arial, Helvetica, sans-serif; color: #000; background: #fff; margin: 0 auto; padding: 16px; font-size: 13px; max-width: 760px; }
  @page { size: A4 portrait; margin: 10mm; }
  @media print {
    body { padding: 0; max-width: none; }
    /* Pedido nao se parte entre folhas: o cliente confere retirada por retirada. */
    .bloco { page-break-inside: avoid; }
  }
  .topo { border-bottom: 3px solid #12294f; padding-bottom: 10px; margin-bottom: 14px; }
  .loja { font-size: 20px; font-weight: 800; color: #12294f; letter-spacing: .5px; }
  .doc { font-size: 15px; font-weight: 700; margin-top: 2px; }
  .cliente { font-size: 14px; margin-top: 6px; }
  .cliente b { font-size: 15px; }
  .emissao { font-size: 11px; color: #555; margin-top: 2px; }
  .bloco { border: 1px solid #cbd5e1; border-radius: 6px; margin-bottom: 10px; overflow: hidden; }
  .bloco-head { background: #eef2f9; padding: 6px 10px; display: flex; align-items: baseline; gap: 10px; border-bottom: 1px solid #cbd5e1; }
  .ped { font-weight: 800; color: #12294f; }
  .meta { color: #475569; font-size: 11.5px; flex: 1; }
  .bloco-total { font-weight: 800; }
  /* table-layout fixed + colgroup: sem isso cada bloco distribui as proprias colunas conforme o
     tamanho da descricao, e Qtd/Unit./Total dancam de pedido pra pedido — o olho perde a coluna do
     dinheiro. Mesmo erro que a tela cometeu com tabela aninhada (DA-416). */
  table.itens { width: 100%; border-collapse: collapse; table-layout: fixed; }
  table.itens td:nth-child(2) { word-break: break-word; }
  table.itens th { text-align: left; font-size: 10.5px; text-transform: uppercase; letter-spacing: .06em; color: #64748b; padding: 4px 10px; font-weight: 600; }
  table.itens td { padding: 4px 10px; border-top: 1px solid #eef2f9; }
  .cod { font-family: 'Courier New', monospace; width: 58px; }
  .num { text-align: right; white-space: nowrap; }
  th.num { text-align: right; }
  .b { font-weight: 700; }
  .vazio { color: #94a3b8; font-style: italic; }
  .total-geral { display: flex; justify-content: space-between; align-items: center; border-top: 3px solid #12294f; margin-top: 12px; padding-top: 10px; font-weight: 800; font-size: 15px; }
  .total-geral .valor { font-size: 19px; }
  .pagina { text-align: right; font-size: 10.5px; color: #64748b; margin-top: 8px; }
</style></head>
<body>
  <!-- Texto ACENTUADO de proposito: o resto do sistema escreve sem acento por convencao interna,
       mas este documento sai da loja e chega ao cliente com o nome dela no cabecalho. -->
  <div class="topo">
    <div class="loja">FIO DE COBRE</div>
    <div class="doc">Extrato de material em aberto</div>
    <div class="cliente">Cliente: <b>${esc(p.clienteNome)}</b></div>
    <div class="emissao">Emitido em ${hoje}</div>
  </div>
  ${blocos}
  ${rodapeTotal}
  ${rodapePagina}
</body></html>`;
}
